import { useAuth } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useMemo } from "react";

import { DiscoveryLandingContent } from "~/components/discovery/discovery-landing-content";
import { PageTransition } from "~/components/page-transition";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import {
  isDiscoveryStateDefault,
  replaceDiscoveryState,
  useDiscoveryState,
} from "~/lib/discovery/state";
import { coachDiscoverySteps } from "~/lib/schemas/coach-discovery";
import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";

const defaultValues = {
  goals: [],
  otherGoal: undefined,
  startTime: "",
  support: "",
  coachingFormats: "",
  coachingApproaches: [],
  coachingPackages: [],
  coachGender: "",
  coachAges: [],
  userPersonality: [],
  coachPersonality: [],
  faithIntegration: [],
  preferredLanguages: ["English"],
  personalContext: "",
  sessionDeliveryMethods: "",
  prefersCoachLocation: "No preference",
  coachLocationRegion: undefined,
  coachLocationCountry: undefined,
  otherFaithIntegration: undefined,
};

/**
 * The signed-in visitor's saved answers. Anonymous visitors start from a blank
 * form: "Start New Search" clears their answers, and filling the form back in
 * from their anonymous row would undo that.
 */
export const getUserPreferencesServerFn = createServerFn({
  method: "GET",
}).handler(async () => {
  const [{ getSessionUser }, { getUserPreferences }] = await Promise.all([
    import("~/lib/server/auth-helpers"),
    import("~/lib/server/user"),
  ]);

  const user = await getSessionUser();
  const userPreferences = user ? await getUserPreferences(user.id) : undefined;
  if (!userPreferences) {
    return defaultValues;
  }
  return transformUserPreferencesToDiscoveryForm(userPreferences);
});

export const Route = createFileRoute("/_public/discovery/steps")({
  loader: async ({ context }) => {
    const defaultUserId = "";

    await context.queryClient.ensureQueryData({
      queryKey: ["userPreferences", defaultUserId],
      queryFn: () => getUserPreferencesServerFn(),
    });
    return {};
  },
  component: DiscoveryStepsLayout,
});

function DiscoveryStepsLayout() {
  const location = useLocation();
  const auth = useAuth();

  const userId = auth.userId || "";

  const stepMatch = useMemo(
    () => location.pathname.match(/\/discovery\/steps\/(\d+)/),
    [location.pathname],
  );
  const currentStepNumber = stepMatch?.[1] ? parseInt(stepMatch[1], 10) : 1;

  const isShowProgressBar = !location.pathname.includes("processing");

  const { data: userPreferences } = useQuery({
    queryKey: ["userPreferences", userId],
    queryFn: () => getUserPreferencesServerFn(),
  });

  const { form: discoveryForm } = useDiscoveryState();
  const stateIsDefault = useMemo(
    () => isDiscoveryStateDefault(discoveryForm),
    [discoveryForm],
  );

  useEffect(() => {
    // For signed-in users, always load database preferences as the source of truth.
    // For anonymous users, we only load preferences if the form is empty to preserve their progress.
    if (!userPreferences || (!userId && !stateIsDefault)) {
      return;
    }

    replaceDiscoveryState(
      transformUserPreferencesToDiscoveryForm(userPreferences),
    );
  }, [stateIsDefault, userPreferences, userId]);

  const progressBar = isShowProgressBar ? (
    <DiscoveryStep.Progress
      totalSteps={coachDiscoverySteps.length}
      currentIndex={Math.max(
        0,
        Math.min(currentStepNumber - 1, coachDiscoverySteps.length - 1),
      )}
    />
  ) : null;

  // Only show landing layout when explicitly on /discovery/steps/1,
  // not on fallback routes like /discovery/steps/processing.
  if (stepMatch?.[1] === "1") {
    return <DiscoveryLandingContent progressBar={progressBar} />;
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
      <Breadcrumb
        className="mb-6"
        items={[
          { label: "Home", href: "/" },
          { label: "Find my dream coach", href: "/discovery" },
        ]}
      />
      {progressBar}
      <div className="relative mt-8 w-full overflow-visible">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </div>
    </div>
  );
}
